import { useParams, useNavigate, useLocation } from "react-router-dom";
import { LeadDetailsContent } from "../leads/details/LeadDetailsContent";
import { useState, useEffect } from "react";
import { FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LeadboxLayout } from "../components/LeadboxLayout";
import { StatusDropdown } from "../leads/details/StatusDropdown";
import BtnNavigateBack from "@/components/ui/btn_navigate_back";
import { LeadDetails } from "@/types/lead";
import { useLead } from "@/hooks/useLead";
import { useVehicleCard } from "@/hooks/useVehicleCard";
import { getLanguage } from "@/utils/languageUtils";
import {
  leadStatusLabels,
  labelToLeadStatus,
  leadToastLabels,
  commonButtonLabels,
  commonMiscLabels,
  vehicleCardUILabels,
  leadUILabels,
} from "@/i18n";
import { LeadStatus } from "@/types/enums";
import { toast } from "sonner";

import { DuplicateVehicleCardDialog } from "../leads/components/DuplicateVehicleCardDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useShowNavigateBack } from "@/hooks/useShowNavigateBack";

const LeadDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const showPageBack = useShowNavigateBack();

  // Dialog states
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateError, setDuplicateError] = useState<{
    regNumber: string;
    existingVehicleCardId: string;
  } | null>(null);
  const [showGuestConfirmDialog, setShowGuestConfirmDialog] = useState(false);

  // Get the lead data from navigation state, fallback to mock data
  const leadFromState = location.state?.lead as LeadDetails;
  const [currentLead, setCurrentLead] = useState<LeadDetails | null>(
    leadFromState || null,
  );

  // Use lead hook for status updates and getting lead data
  const {
    useGetLead,
    updateLeadStatusAsync,
    isUpdatingLeadStatus,
    updateLeadStatusError,
  } = useLead();

  // Use vehicle card hook for creating vehicle cards
  const {
    createVehicleCard,
    isCreatingVehicleCard,
    createVehicleCardError,
    createVehicleCardSuccess,
  } = useVehicleCard();

  const {
    data: leadData,
    isLoading: isLoadingLead,
    error: leadError,
  } = useGetLead(id || "");

  // Update current lead when data is fetched
  useEffect(() => {
    if (leadData) {
      // Always update currentLead when leadData changes (e.g., after masking)
      setCurrentLead(leadData as LeadDetails);
    }
  }, [leadData]);

  const handleCreateFordonskort = async () => {
    if (!id || !currentLead) return;

    // Check if this is a guest lead
    const isGuestLead =
      !!currentLead.owner_guest || !currentLead.owner_customer;

    // If it's a guest lead, show confirmation dialog first
    if (isGuestLead) {
      setShowGuestConfirmDialog(true);
      return;
    }

    // If not a guest lead, proceed directly
    await proceedWithCreateVehicleCard();
  };

  const proceedWithCreateVehicleCard = async () => {
    if (!id || !currentLead) return;

    // Close guest confirmation dialog if open
    setShowGuestConfirmDialog(false);

    // Create vehicle card from lead directly
    // If it's a guest lead, it will be created without owner info
    // Admin can add owner info later via "Redigera ägarinfo" in the VehicleCard page
    try {
      const result = await createVehicleCard({ leadId: id });

      // Update lead status to CONVERTED (technical enum value)
      // TODO: Should this logic not be done by backend?
      await updateLeadStatusAsync({ leadId: id, status: LeadStatus.CONVERTED });

      // Update local state
      setCurrentLead({
        ...currentLead,
        status: LeadStatus.CONVERTED,
      });

      const isGuestLead =
        !!currentLead.owner_guest || !currentLead.owner_customer;
      const vehicleCardLabels = vehicleCardUILabels(getLanguage());
      const message = isGuestLead
        ? vehicleCardLabels.vehicleCardCreatedSuccessGuest.replace(
            "{id}",
            result.vehicleCardId,
          )
        : vehicleCardLabels.vehicleCardCreatedSuccess.replace(
            "{id}",
            result.vehicleCardId,
          );

      toast.success(vehicleCardLabels.vehicleCardCreated, { description: message });

      // Navigate to the new vehicle card (use id if available, otherwise vehicleCardId)
      navigate(
        `/admin/leadbox/vehicle-cards/${result.id || result.vehicleCardId}`,
      );
    } catch (error: unknown) {
      // Check if it's a duplicate error
      if (
        error &&
        typeof error === "object" &&
        "status" in error &&
        error.status === 409
      ) {
        // Check for duplicate vehicle card error
        if (
          "details" in error &&
          typeof error.details === "object" &&
          error.details &&
          "errorCode" in error.details &&
          error.details.errorCode === "DUPLICATE_VEHICLE_CARD"
        ) {
          // Show duplicate dialog
          setDuplicateError({
            regNumber:
              (error.details as { existingRegNumber?: string })
                .existingRegNumber || "Unknown",
            existingVehicleCardId:
              (error.details as { existingVehicleCardId?: string })
                .existingVehicleCardId || "Unknown",
          });
          setShowDuplicateDialog(true);
        } else {
          // Generic 409 conflict error
          const vehicleCardLabels = vehicleCardUILabels(getLanguage());
          const commonLabels = commonMiscLabels(getLanguage());
          toast.error(commonLabels.conflict, {
            description: (error as { message?: string }).message ||
              vehicleCardLabels.duplicateVehicleCardError,
          });
        }
      } else {
        const vehicleCardLabels = vehicleCardUILabels(getLanguage());
        const commonLabels = commonMiscLabels(getLanguage());
        toast.error(commonLabels.errorTitle, {
          description: error instanceof Error
              ? error.message
              : vehicleCardLabels.couldNotCreateVehicleCard,
        });
      }
    }
  };

  // Handle duplicate dialog
  const handleDuplicateDialogClose = () => {
    setShowDuplicateDialog(false);
    setDuplicateError(null);
  };

  const handleMarkAsCompleted = async () => {
    if (!id || !currentLead) return;

    try {
      await updateLeadStatusAsync({ leadId: id, status: LeadStatus.CONVERTED });

      setCurrentLead({
        ...currentLead,
        status: LeadStatus.CONVERTED,
      });

      const leadLabels = leadUILabels(getLanguage());
      const commonLabels = commonMiscLabels(getLanguage());
      toast.success(leadLabels.leadMarkedAsCompleted, { description: leadLabels.leadStatusUpdatedToCompleted });

      setShowDuplicateDialog(false);
      setDuplicateError(null);
    } catch (error) {
      const leadLabels = leadUILabels(getLanguage());
      const commonLabels = commonMiscLabels(getLanguage());
      toast.error(commonLabels.errorTitle, { description: leadLabels.couldNotUpdateLeadStatus });
    }
  };

  const handleStatusChange = async (statusLabel: string) => {
    try {
      // Convert label to technical enum value for API (uses current language)
      const reverseMap = labelToLeadStatus(getLanguage());
      const technicalStatus = reverseMap[statusLabel] || statusLabel;

      // Call the API to update lead status and wait for completion
      await updateLeadStatusAsync({ leadId: id, status: technicalStatus });

      // Update local state
      if (currentLead) {
        setCurrentLead({
          ...currentLead,
          status: technicalStatus,
        });
      }

      const toastLabels = leadToastLabels(getLanguage());
      toast.success(toastLabels.statusUpdated, {
        description: toastLabels.statusUpdatedDescription.replace(
          "{status}",
          statusLabel,
        ),
      });
    } catch (error) {
      const toastLabels = leadToastLabels(getLanguage());
      toast.error(toastLabels.statusUpdateError, { description: toastLabels.statusUpdateErrorDescription });

      throw error;
    }
  };

  // Show loading or error state if no lead data
  if (!currentLead && isLoadingLead) {
    return (
      <LeadboxLayout>
        <div className="mx-auto max-w-5xl min-w-0 space-y-6 py-2">
          {showPageBack && (
            <div className="flex items-center justify-between">
              <BtnNavigateBack />
            </div>
          )}
          <div className="flex justify-center items-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-4 border-blue-500 border-solid mx-auto mb-4"></div>
              <p className="text-muted-foreground">
                {commonMiscLabels(getLanguage()).loadingData}
              </p>
            </div>
          </div>
        </div>
      </LeadboxLayout>
    );
  }

  if (!currentLead) {
    return (
      <LeadboxLayout>
        <div className="mx-auto max-w-5xl min-w-0 space-y-6 py-2">
          {showPageBack && (
            <div className="flex items-center justify-between">
              <BtnNavigateBack />
            </div>
          )}
          <div className="text-center py-8">
            <p className="text-muted-foreground">
              {leadUILabels(getLanguage()).leadNotFound}
            </p>
          </div>
        </div>
      </LeadboxLayout>
    );
  }

  return (
    <LeadboxLayout>
      <div className="mx-auto max-w-5xl min-w-0 space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <BtnNavigateBack
            label={commonButtonLabels(getLanguage()).back}
            to={
              currentLead?.vehicleType === "motorcycle"
                ? "/admin/leads/mc"
                : "/admin/leadbox"
            }
            state={location.state}
          />
          <div className="flex min-w-0 flex-wrap items-center gap-2 sm:justify-end">
            <StatusDropdown
              initialStatus={(() => {
                const labels = leadStatusLabels(getLanguage());
                return (
                  labels[currentLead.status as LeadStatus] ||
                  currentLead.status ||
                  "NEW"
                );
              })()}
              currentTechnicalStatus={currentLead.status}
              onStatusChange={handleStatusChange}
              disabled={isUpdatingLeadStatus}
            />
            {currentLead.status !== LeadStatus.CONVERTED &&
              currentLead.status !== LeadStatus.LOST && (
                <Button
                  onClick={handleCreateFordonskort}
                  disabled={isCreatingVehicleCard}
                  className="bg-blue-600 hover:bg-blue-700 text-white backdrop-blur"
                >
                  <FileText className="h-4 w-4 mr-2 shrink-0" />
                  {isCreatingVehicleCard
                    ? commonButtonLabels(getLanguage()).creating
                    : commonButtonLabels(getLanguage()).createVehicleCard}
                </Button>
              )}
          </div>
        </div>

        <LeadDetailsContent lead={currentLead} />
      </div>

      {/* Duplicate Vehicle Card Dialog */}
      <DuplicateVehicleCardDialog
        isOpen={showDuplicateDialog}
        onClose={handleDuplicateDialogClose}
        onMarkAsCompleted={handleMarkAsCompleted}
        regNumber={duplicateError?.regNumber || ""}
        existingVehicleCardId={duplicateError?.existingVehicleCardId || ""}
      />

      {/* Guest Lead Confirmation Dialog */}
      <AlertDialog
        open={showGuestConfirmDialog}
        onOpenChange={setShowGuestConfirmDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {vehicleCardUILabels(getLanguage()).guestLeadWarningTitle}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {vehicleCardUILabels(getLanguage()).guestLeadWarningDescription}
              <br />
              <br />
              {vehicleCardUILabels(getLanguage()).guestLeadWarningNote}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {commonButtonLabels(getLanguage()).cancel}
            </AlertDialogCancel>
            <AlertDialogAction onClick={proceedWithCreateVehicleCard}>
              {commonMiscLabels(getLanguage()).continue}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </LeadboxLayout>
  );
};

export default LeadDetailsPage;
